export const API_BASE = (import.meta.env.VITE_API_BASE || "/api").replace(/\/$/, "");
export const PUBLIC_BASE = /^https?:\/\//i.test(API_BASE)
  ? API_BASE.replace(/\/api\/?$/, "")
  : window.location.origin;

const TOKEN_KEY = "lenox_access_token";
const USER_KEY = "lenox_user";

export function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function saveAccessToken(token) {
  const value = String(token || "").trim();
  if (!value) throw new Error("Access token is missing");
  localStorage.setItem(TOKEN_KEY, value);
}

export function saveTenantContext(context) {
  if (context && typeof context === "object") localStorage.setItem("lenox_tenant_context", JSON.stringify(context));
}

export function loadTenantContext() {
  try { return JSON.parse(localStorage.getItem("lenox_tenant_context") || "null"); }
  catch { return null; }
}

export function saveSession(loginResponse) {
  const token = loginResponse?.access_token || "";
  const user = loginResponse?.user || null;
  if (!token || !user) throw new Error("Login response is incomplete");
  saveAccessToken(token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  saveTenantContext(loginResponse?.context || null);
  localStorage.setItem("lenox_language", user.language || "de");
  return user;
}

export function loadStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem("lenox_tenant_context");
  window.dispatchEvent(new CustomEvent("lenox-session-cleared"));
}

async function responseError(res, method, path) {
  let detail = "";
  try {
    const body = await res.json();
    detail = body.detail ? `: ${body.detail}` : "";
  } catch {
    try { detail = `: ${await res.text()}`; } catch { detail = ""; }
  }
  if (res.status === 401 && path !== "/auth/login") clearSession();
  return new Error(`${method} ${path} failed (${res.status})${detail}`);
}

async function request(path, options = {}) {
  const token = getAccessToken();
  const method = options.method || "GET";
  const headers = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...options, method, headers });
  if (!res.ok) throw await responseError(res, method, path);
  if (res.status === 204) return null;
  return res.json();
}

export function apiGet(path) {
  return request(path);
}

export function apiPatch(path, payload) {
  return request(path, { method: "PATCH", body: JSON.stringify(payload) });
}

export function apiPost(path, payload = {}) {
  return request(path, { method: "POST", body: JSON.stringify(payload) });
}

export function apiPut(path, payload) {
  return request(path, { method: "PUT", body: JSON.stringify(payload) });
}

export function apiDelete(path) {
  return request(path, { method: "DELETE" });
}

export async function publicRequest(path, options = {}) {
  const method = options.method || "GET";
  const headers = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...options, method, headers });
  if (!res.ok) throw await responseError(res, method, path);
  return res.status === 204 ? null : res.json();
}

export async function uploadForm(path, formData) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  if (!res.ok) throw await responseError(res, "POST", path);
  return res.json();
}


export async function setupRequest(path, { method = "GET", body = null, setupToken = "" } = {}) {
  const headers = { Accept: "application/json" };
  if (body !== null) headers["Content-Type"] = "application/json";
  if (setupToken) headers.Authorization = `Bearer ${setupToken}`;
  const res = await fetch(`${API_BASE}${path}`, { method, headers, ...(body !== null ? { body: JSON.stringify(body) } : {}) });
  if (!res.ok) throw await responseError(res, method, path);
  return res.status === 204 ? null : res.json();
}

export function publicUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${PUBLIC_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

function query(params) {
  const result = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") result.set(key, String(value));
  });
  const value = result.toString();
  return value ? `?${value}` : "";
}


export async function authenticatedBlobUrl(path) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw await responseError(res, "GET", path);
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    mimeType: blob.type || res.headers.get("content-type") || "application/octet-stream",
    size: blob.size,
  };
}

export async function downloadAuthenticated(path, fallbackName = "download") {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw await responseError(res, "GET", path);
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : plain || fallbackName;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function chatWebSocketUrl() {
  const base = /^https?:\/\//i.test(API_BASE) ? new URL(API_BASE) : new URL(API_BASE, window.location.origin);
  base.protocol = base.protocol === "https:" ? "wss:" : "ws:";
  base.pathname = `${base.pathname.replace(/\/$/, "")}/chat-hub/ws`;
  base.search = "";
  return base.toString();
}

export function chatWebSocketProtocols() {
  const token = getAccessToken();
  return token ? ["lenox-chat", `lenox-auth.${token}`] : ["lenox-chat"];
}

function addonPath(addonId, path = "") {
  const id = String(addonId || "");
  if (!/^[a-z0-9]+(?:[._-][a-z0-9]+)+$/.test(id)) throw new Error("Invalid addon id");
  const suffix = String(path || "");
  if (suffix.includes("..")) throw new Error("Invalid addon path");
  return `/addons/${encodeURIComponent(id)}${suffix ? (suffix.startsWith("/") ? suffix : `/${suffix}`) : ""}`;
}

export const api = {
  health: () => apiGet("/health"),
  setupStatus: () => setupRequest("/setup/status"),
  setupSession: (setupToken) => setupRequest("/setup/session", { method: "POST", body: { setup_token: setupToken } }),
  setupChecks: (setupAccessToken) => setupRequest("/setup/checks", { method: "POST", setupToken: setupAccessToken }),
  setupComplete: (payload, setupAccessToken) => setupRequest("/setup/complete", { method: "POST", body: payload, setupToken: setupAccessToken }),
  deploymentModules: () => apiGet("/admin/deployment/modules"),
  deploymentPreflight: (payload) => apiPost("/admin/deployment/preflight", payload),
  createDeploymentNode: (payload) => apiPost("/admin/deployment/nodes", payload),
  deploymentNodes: () => apiGet("/admin/deployment/nodes"),
  deploymentModuleAction: (nodeId, moduleKey, payload) => apiPost(`/admin/deployment/nodes/${nodeId}/modules/${moduleKey}/action`, payload),

  moduleRuntimeOverview: (params = {}) => apiGet(`/module-runtime/overview${query(params)}`),
  effectiveModules: (params = {}) => apiGet(`/module-runtime/effective${query(params)}`),
  moduleClientBootstrap: (params) => apiGet(`/module-runtime/client-bootstrap${query(params)}`),
  resolveModuleDeepLink: (payload) => apiPost("/module-runtime/resolve-deep-link", payload),
  registerModuleDefinitions: (payload) => apiPost("/module-runtime/definitions", payload),
  updateModuleActivation: (moduleKey, payload) => apiPut(`/module-runtime/activations/${encodeURIComponent(moduleKey)}`, payload),
  audienceSegments: () => apiGet("/module-runtime/segments"),
  updateAudienceSegment: (segmentKey, payload) => apiPut(`/module-runtime/segments/${encodeURIComponent(segmentKey)}`, payload),
  moduleOperations: () => apiGet("/module-runtime/operations"),
  enqueueModuleNotification: (payload) => apiPost("/module-runtime/operations/notifications", payload),
  acquireModuleJobLease: (payload) => apiPost("/module-runtime/operations/jobs/lease", payload),
  heartbeatModuleJobLease: (leaseToken, payload) => apiPost(`/module-runtime/operations/jobs/leases/${encodeURIComponent(leaseToken)}/heartbeat`, payload),
  completeModuleJobLease: (leaseToken, payload) => apiPost(`/module-runtime/operations/jobs/leases/${encodeURIComponent(leaseToken)}/complete`, payload),
  runModuleOperationsWatchdog: (payload = { retry_delay_seconds: 30 }) => apiPost("/module-runtime/operations/watchdog", payload),
  updateModuleChannel: (channel, payload) => apiPut(`/module-runtime/operations/channels/${encodeURIComponent(channel)}`, payload),
  updateModuleChannelHealth: (channel, payload) => apiPost(`/module-runtime/operations/channels/${encodeURIComponent(channel)}/health`, payload),

  profileRoles: () => apiGet("/profile-roles/me"),
  enableProfileRole: (roleId, createProfile = true) => apiPost("/profile-roles/me", { role_id: roleId, create_profile: createProfile }),
  profileSurfaces: () => apiGet("/profile-surfaces/me"),
  profileSurfaceEvents: (surfaceId) => apiGet(`/profile-surfaces/${surfaceId}/events`),
  markProfileSurfaceOpen: (surfaceId, section = null) => apiPost(`/profile-surfaces/${surfaceId}/open`, { section }),

  organizerEventRequests: () => apiGet("/portal/organizer/event-requests"),
  createOrganizerEventRequest: (payload) => apiPost("/portal/organizer/event-requests", payload),
  updateOrganizerEventRequest: (id, payload) => apiPatch(`/portal/organizer/event-requests/${id}`, payload),
  deleteOrganizerEventRequest: (id) => apiDelete(`/portal/organizer/event-requests/${id}`),
  submitOrganizerEventRequest: (id) => apiPost(`/portal/organizer/event-requests/${id}/submit`, {}),
  eventRequestsForReview: () => apiGet("/portal/review/event-requests"),
  reviewEventRequest: (id, payload) => apiPatch(`/portal/review/event-requests/${id}`, payload),
  searchArtists: (params = {}) => apiGet(`/portal/artists/search${query(params)}`),
  organizerArtistRequests: () => apiGet("/portal/organizer/artist-requests"),
  createOrganizerArtistRequest: (payload) => apiPost("/portal/organizer/artist-requests", payload),
  djArtistRequests: () => apiGet("/portal/dj/artist-requests"),
  decideDjArtistRequest: (id, payload) => apiPost(`/portal/dj/artist-requests/${id}/decision`, payload),
  clubArtistBookings: (eventId = null) => apiGet(`/portal/club/artist-bookings${query({ event_id: eventId })}`),
  createClubArtistBooking: (payload) => apiPost("/portal/club/artist-bookings", payload),
  updateClubArtistBooking: (id, payload) => apiPatch(`/portal/club/artist-bookings/${id}`, payload),

  uiCatalog: (language = "de") => publicRequest(`/i18n/catalog${query({ language })}`),
  uiCatalogAll: () => apiGet("/i18n/catalog/all"),
  uiCoverage: () => apiGet("/i18n/coverage"),
  updateUiTranslation: (key, payload) => apiPatch(`/i18n/catalog/${encodeURIComponent(key)}`, payload),

  technicianWorkItems: (params = {}) => apiGet(`/mobile/work-items${query(params)}`),
  technicianWorkSummary: () => apiGet("/mobile/work-items/summary"),
  technicianWorkItem: (id) => apiGet(`/mobile/work-items/${id}`),
  createTechnicianWorkItem: (payload) => apiPost("/mobile/work-items", payload),
  updateTechnicianWorkItem: (id, payload) => apiPatch(`/mobile/work-items/${id}`, payload),
  duplicateTechnicianWorkItem: (id, payload = {}) => apiPost(`/mobile/work-items/${id}/duplicate`, payload),
  updateTechnicianManagement: (id, payload) => apiPatch(`/mobile/work-items/${id}/management`, payload),
  uploadTechnicianAttachment: (id, formData) => uploadForm(`/mobile/work-items/${id}/attachments`, formData),
  updateTechnicianAttachment: (id, attachmentId, body) => request(`/mobile/work-items/${id}/attachments/${attachmentId}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteTechnicianAttachment: (id, attachmentId) => apiDelete(`/mobile/work-items/${id}/attachments/${attachmentId}`),
  addTechnicianRelation: (id, payload) => apiPost(`/mobile/work-items/${id}/relations`, payload),
  deleteTechnicianRelation: (id, relationId) => apiDelete(`/mobile/work-items/${id}/relations/${relationId}`),
  addTechnicianHours: (id, payload) => apiPost(`/mobile/work-items/${id}/hours`, payload),
  deleteTechnicianHours: (id, hourId) => apiDelete(`/mobile/work-items/${id}/hours/${hourId}`),
  technicianMaterials: (id) => apiGet(`/mobile/work-items/${id}/materials`),
  addTechnicianMaterial: (id, payload) => apiPost(`/mobile/work-items/${id}/materials`, payload),
  updateTechnicianMaterial: (id, materialId, payload) => apiPatch(`/mobile/work-items/${id}/materials/${materialId}`, payload),
  deleteTechnicianMaterial: (id, materialId) => apiDelete(`/mobile/work-items/${id}/materials/${materialId}`),
  addTechnicianPost: (id, payload) => apiPost(`/mobile/work-items/${id}/posts`, payload),
  updateTechnicianPost: (id, postId, payload) => apiPatch(`/mobile/work-items/${id}/posts/${postId}`, payload),
  uploadTechnicianPostAttachment: (id, postId, formData) => uploadForm(`/mobile/work-items/${id}/posts/${postId}/attachments`, formData),
  technicianMaterialRequests: (params = {}) => apiGet(`/mobile/material-requests${query(params)}`),
  createTechnicianMaterialRequest: (id, payload) => apiPost(`/mobile/work-items/${id}/material-requests`, payload),
  updateTechnicianMaterialRequest: (requestId, payload) => apiPatch(`/mobile/material-requests/${requestId}`, payload),
  deleteTechnicianMaterialRequest: (requestId) => apiDelete(`/mobile/material-requests/${requestId}`),
  submitTechnicianMaterialRequest: (requestId) => apiPost(`/mobile/material-requests/${requestId}/submit`, {}),
  withdrawTechnicianMaterialRequest: (requestId) => apiPost(`/mobile/material-requests/${requestId}/withdraw`, {}),
  manageTechnicianMaterialRequest: (requestId, payload) => apiPatch(`/mobile/material-requests/${requestId}/management`, payload),
  updateTechnicianMaterialRequestItem: (requestId, itemId, payload) => apiPatch(`/mobile/material-requests/${requestId}/items/${itemId}`, payload),
  uploadTechnicianMaterialRequestAttachment: (requestId, formData) => uploadForm(`/mobile/material-requests/${requestId}/attachments`, formData),
  deleteTechnicianMaterialRequestAttachment: (requestId, attachmentId) => apiDelete(`/mobile/material-requests/${requestId}/attachments/${attachmentId}`),
  readiness: () => apiGet("/readiness"),
  bootstrap: () => apiGet("/bootstrap"),
  login: (payload) => apiPost("/auth/login", payload),
  authMe: () => apiGet("/auth/me"),
  authContext: () => apiGet("/auth/context"),
  workspaceBootstrap: (surface = "club") => apiGet(`/workspace/bootstrap${query({ surface })}`),
  systemAdministrationOverview: () => apiGet("/system-administration/overview"),
  switchContext: (payload) => apiPost("/auth/switch-context", payload),
  logout: () => apiPost("/auth/logout", {}),
  changePassword: (payload) => apiPost("/auth/change-password", payload),

  users: () => apiGet("/users"),
  createUser: (payload) => apiPost("/users", payload),
  updateUser: (userId, payload) => apiPatch(`/users/${userId}`, payload),
  resetUserPassword: (userId, payload) => apiPost(`/users/${userId}/reset-password`, payload),
  roles: () => apiGet("/roles"),
  categories: () => apiGet("/categories"),

  systemMessages: (audience = "all") => apiGet(`/system-messages${query({ audience })}`),
  createSystemMessage: (payload) => apiPost("/system-messages", payload),
  events: () => apiGet("/events"),
  eventOperationsBoard: () => apiGet("/event-operations/board"),
  eventOperations: (eventId) => apiGet(`/event-operations/${eventId}`),
  transitionEventOperations: (eventId, payload) => apiPost(`/event-operations/${eventId}/transition`, payload),
  eventProduction: (eventId) => apiGet(`/event-operations/${eventId}/production`),
  handoffEventWorkItem: (eventId, workItemId, payload) => apiPost(`/event-operations/${eventId}/work-items/${workItemId}/handoff`, payload),
  closeoutEventProduction: (eventId, payload) => apiPost(`/event-operations/${eventId}/closeout`, payload),
  createEvent: (payload) => apiPost("/events", payload),
  eventGenres: (q = "") => apiGet(`/event-genres${query({ q })}`),
  createEventGenre: (payload) => apiPost("/event-genres", payload),
  updateEvent: (eventId, payload) => apiPatch(`/events/${eventId}`, payload),
  exportEvent: (eventId) => apiGet(`/events/${eventId}/export`),
  eventAssets: (eventId) => apiGet(`/events/${eventId}/assets`),
  uploadEventAsset: (eventId, formData) => uploadForm(`/events/${eventId}/assets`, formData),

  topics: (categoryId) => apiGet(`/topics${query({ category_id: categoryId })}`),
  topic: (id) => apiGet(`/topics/${id}`),
  createTopic: (payload) => apiPost("/topics", payload),
  createPost: (topicId, payload) => apiPost(`/topics/${topicId}/posts`, payload),
  createScheduleRequest: (payload) => apiPost("/schedule-requests", payload),
  requestSoundcheck: (payload) => apiPost("/soundcheck/request", payload),
  forwardSoundcheckFromInbox: (itemId, payload) => apiPost(`/soundcheck/inbox/${itemId}/forward`, payload),
  confirmSoundcheckFromInbox: (itemId, payload) => apiPost(`/soundcheck/inbox/${itemId}/confirm`, payload),
  declineSoundcheckFromInbox: (itemId, payload) => apiPost(`/soundcheck/inbox/${itemId}/decline`, payload),
  counterSoundcheckFromInbox: (itemId, payload) => apiPost(`/soundcheck/inbox/${itemId}/counter`, payload),
  createRepairCase: (payload) => apiPost("/repair-cases", payload),
  createDamageCase: (payload) => apiPost("/damage-cases", payload),
  createMaterialRequest: (payload) => apiPost("/material-requests", payload),
  createHourEntry: (payload) => apiPost("/hour-entries", payload),

  communicationCounts: () => apiGet("/communication/counts"),
  messageContacts: (params = {}) => apiGet(`/message-center/contacts${query(params)}`),
  messageCenter: (folder = "inbox", q = "") => apiGet(`/message-center/messages${query({ folder, q })}`),
  messageCenterItem: (id) => apiGet(`/message-center/messages/${id}`),
  messageThread: (threadId) => apiGet(`/message-center/threads/${threadId}`),
  sendMessageCenter: (formData) => uploadForm("/message-center/send", formData),
  replyMessageCenter: (id, formData) => uploadForm(`/message-center/messages/${id}/reply`, formData),
  patchMessageState: (id, payload) => apiPatch(`/message-center/messages/${id}/state`, payload),

  chatHubBootstrap: () => apiGet("/chat-hub/bootstrap"),
  chatHubMessages: (params = {}) => apiGet(`/chat-hub/messages${query(params)}`),
  createChatHubMessage: (payload) => apiPost("/chat-hub/messages", payload),
  uploadChatHubAttachments: (id, formData) => uploadForm(`/chat-hub/messages/${id}/attachments`, formData),
  markChatHubRead: (payload) => apiPost("/chat-hub/read", payload),

  inbox: (userId = null, targetLanguage = "") => apiGet(`/inbox${query({ user_id: userId, target_language: targetLanguage })}`),
  createInbox: (payload) => apiPost("/inbox", payload),
  markInboxRead: (id, targetLanguage = "") => apiPost(`/inbox/${id}/mark-read${query({ target_language: targetLanguage })}`, {}),
  replyInbox: (id, payload) => apiPost(`/inbox/${id}/reply`, payload),
  chatMessages: (channel = "ops", userId = null) => apiGet(`/chat/messages${query({ channel, user_id: userId })}`),
  createChatMessage: (payload) => apiPost("/chat/messages", payload),

  organizers: (q = "") => apiGet(`/organizers${query({ q })}`),
  createOrganizer: (payload) => apiPost("/organizers", payload),
  updateOrganizer: (organizerId, payload) => apiPatch(`/organizers/${organizerId}`, payload),
  createOrganizerWithLogin: (payload) => apiPost("/organizers/with-login", payload),
  artists: (organizerId = null, q = "") => apiGet(`/artists${query({ organizer_id: organizerId, q })}`),
  createArtist: (payload) => apiPost("/artists", payload),
  updateArtist: (artistId, payload) => apiPatch(`/artists/${artistId}`, payload),
  createArtistWithLogin: (payload) => apiPost("/artists/with-login", payload),
  eventPotentials: (date = null) => apiGet(`/event-potentials${query({ date })}`),
  createEventPotential: (payload) => apiPost("/event-potentials", payload),
  eventWorkspace: (eventId) => apiGet(`/events/${eventId}/workspace`),
  createEventParticipant: (payload) => apiPost("/event-participants", payload),
  deleteEventParticipant: (participantId) => apiDelete(`/event-participants/${participantId}`),
  createEventSpecialRequest: (payload) => apiPost("/event-special-requests", payload),
  createEventAccessEntry: (payload) => apiPost("/event-access-entries", payload),
  createRiderStatus: (payload) => apiPost("/event-rider-status", payload),

  controlActions: (eventId = null) => apiGet(`/control/actions${query({ event_id: eventId })}`),
  createControlAction: (payload) => apiPost("/control/actions", payload),
  convertControlActionToTopic: (actionId) => apiPost(`/control/actions/${actionId}/convert-to-topic`, {}),
  displayState: (scope = "club_main") => apiGet(`/display/state${query({ scope })}`),
  saveDisplayState: (payload) => apiPost("/display/state", payload),
  eventChat: (eventId, userId = null, language = "") => apiGet(`/event-chat${query({ event_id: eventId, user_id: userId, language })}`),
  createEventChatMessage: (payload) => apiPost("/event-chat", payload),
  search: (q) => apiGet(`/search${query({ q })}`),

  translateText: (payload) => apiPost("/translate", payload),
  pretranslateText: (payload) => apiPost("/translate/pretranslate", payload),
  translateInbox: (itemId, targetLanguage, force = false) => apiPost(`/translate/inbox/${itemId}${query({ target_language: targetLanguage, force })}`, {}),
  translateChatMessage: (messageId, targetLanguage, force = false) => apiPost(`/translate/chat/${messageId}${query({ target_language: targetLanguage, force })}`, {}),
  translationCache: (entityType = "", targetLanguage = "") => apiGet(`/translate/cache${query({ entity_type: entityType, target_language: targetLanguage })}`),
  translationStatus: () => apiGet("/translate/status"),
  adminTranslationSettings: () => apiGet("/admin/translation-settings"),
  updateAdminTranslationSettings: (payload) => apiPatch("/admin/translation-settings", payload),
  administrationOverview: () => apiGet("/admin/overview"),
  administrationInstallation: () => apiGet("/admin/installation"),
  updateAdministrationInstallation: (payload) => apiPatch("/admin/installation", payload),
  administrationOrganizations: () => apiGet("/admin/organizations"),
  createAdministrationOrganization: (payload) => apiPost("/admin/organizations", payload),
  updateAdministrationOrganization: (id, payload) => apiPatch(`/admin/organizations/${id}`, payload),
  administrationClubs: (organizationId = null) => apiGet(`/admin/clubs${query({ organization_id: organizationId })}`),
  createAdministrationClub: (payload) => apiPost("/admin/clubs", payload),
  updateAdministrationClub: (id, payload) => apiPatch(`/admin/clubs/${id}`, payload),
  administrationVenues: (clubId = null) => apiGet(`/admin/venues${query({ club_id: clubId })}`),
  createAdministrationVenue: (payload) => apiPost("/admin/venues", payload),
  updateAdministrationVenue: (id, payload) => apiPatch(`/admin/venues/${id}`, payload),
  administrationAreas: (clubId) => apiGet(`/admin/clubs/${clubId}/areas`),
  createAdministrationArea: (venueId, payload) => apiPost(`/admin/venues/${venueId}/areas`, payload),
  updateAdministrationArea: (id, payload) => apiPatch(`/admin/venue-areas/${id}`, payload),
  administrationBranding: (clubId) => apiGet(`/admin/clubs/${clubId}/branding`),
  updateAdministrationBranding: (clubId, payload) => apiPatch(`/admin/clubs/${clubId}/branding`, payload),
  administrationFeatures: (clubId) => apiGet(`/admin/clubs/${clubId}/features`),
  updateAdministrationFeatures: (clubId, features) => apiPatch(`/admin/clubs/${clubId}/features`, { features }),
  administrationUsers: () => apiGet("/admin/users"),
  administrationMemberships: (userId) => apiGet(`/admin/users/${userId}/memberships`),
  updateAdministrationMemberships: (userId, payload) => apiPatch(`/admin/users/${userId}/memberships`, payload),
  administrationAudit: (params = {}) => apiGet(`/admin/audit-events${query(params)}`),
  administrationOperationsReadiness: () => apiGet("/admin/operations/readiness"),
  onboardingCatalog: () => apiGet("/onboarding/catalog"),
  onboardingState: (tutorialKey) => apiGet(`/onboarding/state/${encodeURIComponent(tutorialKey)}`),
  updateOnboardingState: (tutorialKey, payload) => apiPatch(`/onboarding/state/${encodeURIComponent(tutorialKey)}`, payload),
  restartOnboarding: (tutorialKey) => apiPost(`/onboarding/state/${encodeURIComponent(tutorialKey)}/restart`, {}),
  accountsDirectory: (q = "", clubId = null) => apiGet(`/admin/accounts${query({ q, club_id: clubId })}`),
  accountDirectoryEntry: (userId) => apiGet(`/admin/accounts/${userId}`),
  createDirectoryAccount: (payload) => apiPost("/admin/accounts", payload),
  updateDirectoryAccount: (userId, payload) => apiPatch(`/admin/accounts/${userId}`, payload),
  ownershipTransfers: (clubId) => apiGet(`/admin/clubs/${clubId}/ownership-transfers`),
  createOwnershipTransfer: (clubId, payload) => apiPost(`/admin/clubs/${clubId}/ownership-transfers`, payload),
  ownershipTransferAction: (transferId, action) => apiPost(`/admin/ownership-transfers/${transferId}/${action}`, {}),
  setupCenterSummary: () => apiGet("/admin/setup-center/summary"),
  setupCenterModules: (clubId = null) => apiGet(`/admin/setup-center/modules${query({ club_id: clubId })}`),
  updateSetupCenterModule: (featureKey, payload) => apiPatch(`/admin/setup-center/modules/${encodeURIComponent(featureKey)}`, payload),
  updatePacks: () => apiGet("/admin/setup-center/update-packs"),
  uploadUpdatePack: (formData) => uploadForm("/admin/setup-center/update-packs", formData),
  downloadUpdatePack: (packId, filename = "lenox-updatepack.tar.xz") => downloadAuthenticated(`/admin/setup-center/update-packs/${packId}/download`, filename),
  addonRuntime: () => apiGet("/addons/runtime"),
  addonGet: (addonId, path = "") => apiGet(addonPath(addonId, path)),
  addonPost: (addonId, path, payload = {}) => apiPost(addonPath(addonId, path), payload),
  addonPatch: (addonId, path, payload = {}) => apiPatch(addonPath(addonId, path), payload),
  addonDelete: (addonId, path) => apiDelete(addonPath(addonId, path)),
  addonUpload: (addonId, path, formData) => uploadForm(addonPath(addonId, path), formData),
  addons: () => apiGet("/admin/addons"),
  addonPublishers: () => apiGet("/admin/addons/publishers"),
  trustAddonPublisher: (payload) => apiPost("/admin/addons/publishers", payload),
  revokeAddonPublisher: (publisherId) => apiPost(`/admin/addons/publishers/${encodeURIComponent(publisherId)}/revoke`, {}),
  addonPackages: () => apiGet("/admin/addons/packages"),
  uploadAddonPackage: (formData) => uploadForm("/admin/addons/packages", formData),
  downloadAddonPackage: (packageId, filename = "lenox-addon.tar.xz") => downloadAuthenticated(`/admin/addons/packages/${packageId}/download`, filename),
  addonHealth: (addonId) => apiGet(`/admin/addons/${encodeURIComponent(addonId)}/health`),
  clubAddons: (clubId) => apiGet(`/admin/clubs/${clubId}/addons`),
  updateClubAddon: (clubId, addonId, payload) => apiPatch(`/admin/clubs/${clubId}/addons/${encodeURIComponent(addonId)}`, payload),

  clubEquipment: () => apiGet("/club/equipment"),
  updateClubEquipment: (itemId, payload) => apiPatch(`/club/equipment/${itemId}`, payload),

  technicalRiderBootstrap: (eventId = null) => apiGet(`/technical-rider/bootstrap${query({ event_id: eventId })}`),
  updateTechnicalRiderSettings: (payload) => apiPatch("/technical-rider/settings", payload),
  uploadTechnicalRiderBoothImage: (formData) => uploadForm("/technical-rider/settings/booth-image", formData),
  createTechnicalRiderRule: (payload) => apiPost("/technical-rider/rules", payload),
  updateTechnicalRiderRule: (ruleId, payload) => apiPatch(`/technical-rider/rules/${ruleId}`, payload),
  acknowledgeTechnicalRiderRule: (ruleId, payload = {}) => apiPost(`/technical-rider/rules/${ruleId}/acknowledge`, payload),
  technicalRiderEquipment: () => apiGet("/technical-rider/equipment"),
  createTechnicalRiderEquipment: (payload) => apiPost("/technical-rider/equipment", payload),
  updateTechnicalRiderEquipment: (equipmentId, payload) => apiPatch(`/technical-rider/equipment/${equipmentId}`, payload),
  uploadTechnicalRiderEquipmentMedia: (equipmentId, formData) => uploadForm(`/technical-rider/equipment/${equipmentId}/media`, formData),
  deleteTechnicalRiderEquipmentMedia: (mediaId) => apiDelete(`/technical-rider/equipment-media/${mediaId}`),
  myDjTechnicalProfile: () => apiGet("/technical-rider/dj/profile"),
  saveMyDjTechnicalProfile: (payload) => apiPut("/technical-rider/dj/profile", payload),
  myDjTechnicalEventPlan: (eventId) => apiGet(`/technical-rider/dj/events/${eventId}`),
  saveMyDjTechnicalEventPlan: (eventId, payload) => apiPut(`/technical-rider/dj/events/${eventId}`, payload),
  myDjEventAllowance: (eventId) => apiGet(`/technical-rider/dj/events/${eventId}/allowance`),
  organizerRiderArtists: (eventId) => apiGet(`/technical-rider/organizer/events/${eventId}/artists`),
  saveOrganizerArtistAllowance: (eventId, planId, payload) => apiPut(`/technical-rider/organizer/events/${eventId}/artists/${planId}/allowance`, payload),
  decideOrganizerArtistGuest: (eventId, guestId, payload) => apiPatch(`/technical-rider/organizer/events/${eventId}/guests/${guestId}`, payload),

  myDjPortalProfile: () => apiGet("/portal/dj/me"),
  saveMyDjPortalProfile: (payload) => apiPut("/portal/dj/me", payload),
  myDjEventPlan: (eventId) => apiGet(`/portal/dj/events/${eventId}/plan`),
  saveMyDjEventPlan: (eventId, payload) => apiPut(`/portal/dj/events/${eventId}/plan`, payload),
  addMyDjGuest: (eventId, payload) => apiPost(`/portal/dj/events/${eventId}/guests`, payload),
  deleteMyDjGuest: (eventId, guestId) => apiDelete(`/portal/dj/events/${eventId}/guests/${guestId}`),

  myOrganizerPortalProfile: () => apiGet("/portal/organizer/me"),
  saveMyOrganizerPortalProfile: (payload) => apiPut("/portal/organizer/me", payload),
  myOrganizerEventPlan: (eventId) => apiGet(`/portal/organizer/events/${eventId}/plan`),
  saveMyOrganizerEventPlan: (eventId, payload) => apiPut(`/portal/organizer/events/${eventId}/plan`, payload),

  uploadPortalProfileMedia: (formData) => uploadForm("/portal/profile-media", formData),
  artistPlansForReview: (eventId = null) => apiGet(`/portal/review/artist-plans${query({ event_id: eventId })}`),
  reviewArtistPlan: (planId, payload) => apiPatch(`/portal/review/artist-plans/${planId}`, payload),
  organizerPlansForReview: (eventId = null) => apiGet(`/portal/review/organizer-plans${query({ event_id: eventId })}`),
  reviewOrganizerPlan: (planId, payload) => apiPatch(`/portal/review/organizer-plans/${planId}`, payload),


  whatsappAuthStatus: () => publicRequest("/whatsapp-auth/status"),
  whatsappOtpRequest: (payload) => publicRequest("/whatsapp-auth/request", { method: "POST", body: JSON.stringify(payload) }),
  whatsappOtpVerify: (payload) => publicRequest("/whatsapp-auth/verify", { method: "POST", body: JSON.stringify(payload) }),
  whatsappSettings: () => apiGet("/admin/whatsapp/settings"),
  updateWhatsappSettings: (payload) => apiPatch("/admin/whatsapp/settings", payload),
  updateWhatsappContact: (userId, payload) => apiPut(`/admin/whatsapp/contacts/${userId}`, payload),
  whatsappStatus: () => apiGet("/admin/whatsapp/status"),
  whatsappSendDiagnostic: () => apiGet("/admin/whatsapp/diagnostics/send"),
  whatsappJobs: (limit = 100) => apiGet(`/admin/whatsapp/jobs${query({ limit })}`),
  deleteWhatsappJob: (jobId) => apiDelete(`/admin/whatsapp/jobs/${jobId}`),
  deleteWhatsappJobs: (status) => apiDelete(`/admin/whatsapp/jobs${query({ status })}`),
  retryWhatsappJob: (jobId) => apiPost(`/admin/whatsapp/jobs/${jobId}/retry`, {}),
  whatsappSessionCommand: (command) => apiPost(`/admin/whatsapp/session/${command}`, {}),
  whatsappSendTest: (payload) => apiPost("/admin/whatsapp/send-test", payload),
  messagingSendTest: (payload) => apiPost("/admin/messaging/send-test", payload),
  whatsappSendInvitation: (payload) => apiPost("/admin/whatsapp/send-invitation", payload),
  infrastructureSettings: () => apiGet("/admin/infrastructure/settings"),
  updateInfrastructureSettings: (payload) => apiPatch("/admin/infrastructure/settings", payload),
  infrastructureStatus: () => apiGet("/admin/infrastructure/status"),
  infrastructureNodeLogs: (nodeId, service, lines = 120) => apiGet(`/admin/infrastructure/nodes/${nodeId}/logs${query({ service, lines })}`),
  renderVpnPeer: (payload) => apiPost("/admin/infrastructure/vpn/render-peer", payload),

  portalInvitations: () => apiGet("/portal/invitations"),
  createPortalInvitation: (payload) => apiPost("/portal/invitations", payload),
  revokePortalInvitation: (id) => apiDelete(`/portal/invitations/${id}`),
  inspectInvitation: (token) => publicRequest(`/public/invitations/${encodeURIComponent(token)}`),
  acceptInvitation: (token, payload) => publicRequest(`/public/invitations/${encodeURIComponent(token)}/accept`, { method: "POST", body: JSON.stringify(payload) }),
};
